/**
 * An in-memory `ItemCardSource` (`specs/M1-B7-item-card-and-updates.md` §11).
 *
 * The seed is the **sandbox item the round-5 capture was taken from** — five updates, two
 * replies, one like, one attachment — so `/__ui/board?card=<item>` renders exactly the state
 * the reference screenshots show, with no api, no database and no session.
 */
import { statusPalette } from '@elizmonday/ui';
import type {
  ActivityFacets,
  ActivityQuery,
  CardActivityRow,
  CardAsset,
  CardMention,
  CardPerson,
  CardSubitem,
  CardUpdate,
  CardUpdateReader,
  ItemCardHeader,
  ItemCardSource,
} from './types';

/**
 * The group colours the capture shows, taken from monday's own status palette rather than
 * written as raw hex (M0-F7 U1): the sandbox's groups are `working_orange`, `grass_green`
 * and `stuck_red` `[ic§Board-level activity log]`.
 */
const GROUP_ORANGE = statusPalette.working_orange.base;
const GROUP_GREEN = statusPalette.grass_green.base;
const GROUP_RED = statusPalette.stuck_red.base;

const ME: CardPerson = { id: '3733063', name: 'Roberto', photoUrl: null };
const OTHER: CardPerson = { id: '4708039', name: 'Dana Levi', photoUrl: null };
/**
 * **P-317.** The person the verifier's own outcome is written about: *post an update, have the QA
 * member read it, and the author sees `QA member` in the list*. In the product that read is an
 * `update_reads` row the Update feed writes when that person opens the feed; here the fixture
 * stands in for the feed, so a freshly posted update arrives with exactly that one reader.
 */
const QA_MEMBER: CardPerson = { id: '4708100', name: 'QA member', photoUrl: null };

/** A 16 × 9 PNG — enough for the inline-image check to hit-test a painted box (P-028). */
const IMAGE_DATA_URI =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAJCAIAAAC0SDtlAAAAF0lEQVR42mM8du0NAymAiYFEMKqBJhoAaIgCmkAhvRwAAAAASUVORK5CYII=';

const minutesAgo = (minutes: number): string => new Date(Date.now() - minutes * 60_000).toISOString();

function update(partial: Partial<CardUpdate> & Pick<CardUpdate, 'id' | 'bodyHtml' | 'bodyText'>): CardUpdate {
  return {
    itemId: 'F-ITEM-1',
    parentId: null,
    createdAt: minutesAgo(10),
    updatedAt: minutesAgo(10),
    editedAt: null,
    pinned: false,
    pinnedAt: null,
    creator: ME,
    mentions: [],
    likes: [],
    assets: [],
    replies: [],
    ...partial,
  };
}

/** The five bodies the capture posted, verbatim `[ua:a03]`–`[ua:a08]`. */
function seedUpdates(): CardUpdate[] {
  const first = update({
    id: 'F-U-1',
    bodyHtml: '<p>Edited body</p>',
    bodyText: 'Edited body',
    createdAt: minutesAgo(21),
    editedAt: minutesAgo(20),
    likes: [{ userId: ME.id, reactionType: 'like', createdAt: minutesAgo(20) }],
    assets: [
      { id: 'F-A-1', name: 'r5-probe.txt', mime: 'text/plain', size: 46, url: '/files/F-A-1', thumbnailUrl: null, origin: 'update', createdAt: minutesAgo(19) },
      /* P-028: the image attachment the parity shot compares — inline, not a file-name link.
         A data URI so `/__ui/board` still needs no server. */
      { id: 'F-A-2', name: 'image.png', mime: 'image/png', size: 150_528, url: IMAGE_DATA_URI, thumbnailUrl: null, origin: 'update', createdAt: minutesAgo(19) },
    ],
    /* P-029: monday's `👁 n`. No server serves this yet — see `CardUpdate.viewsCount`. */
    viewsCount: 1,
    replies: [
      update({ id: 'F-U-1-R1', parentId: 'F-U-1', bodyHtml: '<p>Edited reply</p>', bodyText: 'Edited reply', createdAt: minutesAgo(18) }),
      update({
        id: 'F-U-1-R2',
        parentId: 'F-U-1',
        bodyHtml: '<p>Second reply, item_id also given</p>',
        bodyText: 'Second reply, item_id also given',
        createdAt: minutesAgo(17),
        creator: OTHER,
      }),
    ],
  });
  return [
    update({
      id: 'F-U-5',
      bodyHtml: '<p>Literal @Roberto in the body</p>',
      bodyText: 'Literal @Roberto in the body',
      createdAt: minutesAgo(6),
    }),
    update({
      id: 'F-U-4',
      bodyHtml:
        '<p>Board mention</p> <a class="user_mention_editor router" href="/boards/F-BOARD" data-mention-type="Board" data-mention-id="F-BOARD" target="_blank" rel="noopener noreferrer">@Sandbox – Column types</a>',
      bodyText: 'Board mention\n\n@Sandbox – Column types',
      createdAt: minutesAgo(7),
      mentions: [{ kind: 'board', id: 'F-BOARD', name: 'Sandbox – Column types' }],
    }),
    update({
      id: 'F-U-3',
      bodyHtml:
        '<p>Hello there</p> <a class="user_mention_editor router" href="/users/3733063" data-mention-type="User" data-mention-id="3733063" target="_blank" rel="noopener noreferrer">@Roberto</a>',
      bodyText: 'Hello there\n\n@Roberto',
      createdAt: minutesAgo(8),
      mentions: [{ kind: 'user', id: ME.id, name: 'Roberto' }],
    }),
    update({
      id: 'F-U-2',
      bodyHtml:
        '<p>Rich <strong>bold</strong> <em>italic</em> <u>underline</u> strike <a href="https://example.com" target="_blank" rel="noopener noreferrer">link</a></p><ul><li>one</li><li>two</li></ul><ol><li>first</li></ol><blockquote>quoted</blockquote><p>😀 emoji</p>',
      bodyText: 'Rich bold italic underline strike link\n\n* one\n* two\n* first\nquoted😀 emoji',
      createdAt: minutesAgo(9),
    }),
    first,
  ];
}

/** Rows shaped like the ones the board log drew in the capture `[ic§Board-level activity log]`. */
function seedActivity(): CardActivityRow[] {
  const row = (
    id: string,
    event: string,
    label: string,
    entityName: string,
    extra: Partial<CardActivityRow['rendered']> = {},
    minutes = 19,
  ): CardActivityRow => ({
    id,
    event,
    createdAt: minutesAgo(minutes),
    user: ME,
    rendered: {
      label,
      icon: 'activity',
      entityName,
      entityKind: 'item',
      before: null,
      after: null,
      detail: null,
      revertible: false,
      deleted: false,
      color: null,
      ...extra,
    },
  });
  return [
    row('F-L-1', 'delete_group', 'Group Deleted', 'R5 probe group', { deleted: true, color: GROUP_ORANGE }, 19),
    row('F-L-2', 'create_group', 'Group Created', 'R5 probe group', { color: GROUP_ORANGE }, 19),
    row('F-L-3', 'delete_column', 'Column Deleted', 'R5 probe col', {}, 19),
    row('F-L-4', 'delete_pulse', 'Deleted', 'R5 delete probe', { revertible: true, deleted: true }, 19),
    row('F-L-5', 'create_pulse', 'Created', 'R5 delete probe', { detail: 'Group: Group 1' }, 19),
    row('F-L-6', 'archive_pulse', 'Archived', 'R5 activity probe renamed', { revertible: true }, 19),
    row('F-L-7', 'create_column', 'Column Created', 'R5 probe col', {}, 19),
    row('F-L-8', 'move_pulse_from_group', 'Moved', 'R5 activity probe renamed', { before: 'Group 1', after: 'Group 2', revertible: true }, 19),
    row('F-L-9', 'update_name', 'Name', 'R5 activity probe renamed', { before: 'R5 activity probe', after: 'R5 activity probe renamed' }, 19),
    row('F-L-10', 'update_column_value', 'Text (renamed)', 'R5 activity probe', { before: 'activity probe text', after: 'batch write' }, 20),
    row('F-L-11', 'update_column_value', 'Numbers', 'R5 activity probe', { before: null, after: '$42.00' }, 20),
  ];
}

export interface FixtureItemCardOptions {
  itemId?: string;
  boardId?: string;
  boardName?: string;
  itemName?: string;
  /** Start with no updates, to render the empty state. */
  empty?: boolean;
  /**
   * **P-308.** Make the subitems read (and `+ Add subitem`) fail with this sentence — the
   * fixture for a board whose subitems board is unreachable, which on the pilot answered
   * *"Board 6061227186 not found"*. The card must still open.
   */
  subitemsError?: string;
}

export function createFixtureItemCardSource(options: FixtureItemCardOptions = {}): ItemCardSource {
  const itemId = options.itemId ?? 'F-ITEM-1';
  const boardId = options.boardId ?? 'F-BOARD';
  const boardName = options.boardName ?? 'Sandbox – Column types';
  const itemName = options.itemName ?? 'R5 updates probe 2026-09-10T14:02:00.082Z';
  /** P-060: the card renames in place, so the header's name is state, not a constant. */
  let currentName = itemName;

  let updates = options.empty ? [] : seedUpdates().map((row) => ({ ...row, itemId }));
  /** P-162: the harness's parent has one subitem, so the section, its rows and `+` all paint. */
  let subitems: CardSubitem[] = options.empty
    ? []
    : [{ id: 'F-SUBITEM-1', name: 'Draft the migration note', boardId: 'F-BOARD-SUB' }];
  /** P-304: what a delete took off the section, so its **Undo** can put that row back. */
  const removedSubitems = new Map<string, { row: CardSubitem; at: number }>();
  const activity = seedActivity();
  let assets: CardAsset[] = options.empty
    ? []
    : [
        {
          id: 'F-A-1',
          name: 'r5-probe.txt',
          mime: 'text/plain',
          size: 46,
          url: '/files/F-A-1',
          thumbnailUrl: null,
          origin: 'update',
          createdAt: minutesAgo(19),
        },
      ];
  let nextId = 100;

  /**
   * **P-317** — the readers behind each `👁 n`, keyed by update id and in **read order**, which is
   * the order the service answers in. `F-U-1` carries `viewsCount: 1`, so it carries exactly one
   * reader, and it is not its own author: P-315 keeps the author out of its readership.
   */
  const readers = new Map<string, CardUpdateReader[]>(
    options.empty
      ? []
      : [['F-U-1', [{ id: OTHER.id, name: OTHER.name, photoUrl: OTHER.photoUrl, readAt: minutesAgo(15) }]]],
  );

  const find = (updateId: string): CardUpdate | null => {
    for (const row of updates) {
      if (row.id === updateId) return row;
      const reply = row.replies.find((entry) => entry.id === updateId);
      if (reply) return reply;
    }
    return null;
  };

  const html = (body: string, mentions: readonly CardMention[]): string =>
    mentions.length === 0
      ? body
      : `${body} ${mentions
          .map(
            (mention) =>
              `<a class="user_mention_editor router" href="/users/${mention.id}" data-mention-type="${mention.kind === 'user' ? 'User' : mention.kind === 'board' ? 'Board' : mention.kind === 'team' ? 'Team' : 'Project'}" data-mention-id="${mention.id}" target="_blank" rel="noopener noreferrer">@${mention.name}</a>`,
          )
          .join(' ')}`;

  const text = (body: string): string => body.replace(/<[^>]*>/g, '').trim();

  return {
    async header(): Promise<ItemCardHeader> {
      return {
        id: itemId,
        name: currentName,
        boardId,
        boardName,
        groupTitle: 'Group 1',
        updatesCount: updates.length,
      };
    },
    async updates() {
      return updates.map((row) => ({ ...row, replies: [...row.replies] }));
    },
    async assets() {
      return [...assets];
    },
    /** P-317: the readers of one update, in read order, as `updates.readers` answers. */
    async updateReaders({ updateId }): Promise<CardUpdateReader[]> {
      return (readers.get(updateId) ?? []).map((row) => ({ ...row }));
    },
    async activity(query: ActivityQuery) {
      const rows = query.itemIds?.length ? activity.slice(0, 2) : activity;
      return { rows, total: rows.length };
    },
    async facets(): Promise<ActivityFacets> {
      const counts = new Map<string, number>();
      for (const row of activity) counts.set(row.event, (counts.get(row.event) ?? 0) + 1);
      return {
        total: activity.length,
        time: [
          { key: 'today', label: 'Today', count: activity.length },
          { key: 'yesterday', label: 'Yesterday', count: 0 },
          { key: 'week', label: 'This Week', count: activity.length },
          { key: 'month', label: 'This Month', count: activity.length },
          { key: 'year', label: 'This Year', count: activity.length },
        ],
        group: [
          { key: 'topics', label: 'Group 1', count: 7, color: GROUP_GREEN },
          { key: 'group_2', label: 'Group 2', count: 4, color: GROUP_RED },
        ],
        column: [
          { key: 'text', label: 'Text (renamed)', count: 1 },
          { key: 'numbers', label: 'Numbers', count: 1 },
        ],
        person: [{ key: ME.id, label: ME.name, count: activity.length }],
        event: [...counts].map(([key, count]) => ({ key, label: key, count })),
      };
    },
    me: () => ME,
    async mentionCandidates(query: string) {
      const term = query.trim().toLowerCase();
      return [ME, OTHER].filter((person) => person.name.toLowerCase().includes(term));
    },
    /**
     * **P-303 — the harness answers a write the way the *wire* does, not the way the feed does.**
     *
     * Nico's screenshot: a just-posted update and its reply both read "? Unknown · now", right
     * until a reload. The cause was the api answering the raw `updates` row — a `created_by`
     * id and no person — while `updates.list` answers a joined `UpdateView`; the card inserts
     * a write's answer as it stands. The api now answers the view (`services/updates.ts`,
     * covered by `b7-b8.test.ts`), and the **client** keeps a guard for the answers that do
     * not: an older deployment, and the realtime payload, which never carried a creator.
     *
     * So the fixture deliberately answers **without a creator**. That is what makes
     * `verify-item-card-local.mjs` prove the half that lives in the browser: the author's name
     * appears on the posted card with no reload and no api at all.
     */
    async createUpdate({ body, mentions }) {
      const row = update({
        id: `F-U-new-${String(nextId++)}`,
        itemId,
        bodyHtml: html(body, mentions),
        bodyText: text(body),
        createdAt: new Date().toISOString(),
        mentions: [...mentions],
      });
      // The **feed** holds the row with its author, exactly as a list read answers it …
      updates = [row, ...updates];
      /*
       * P-317: and the feed has marked it read for the QA member, so the new update's `👁 1`
       * has a name behind it. `viewsCount` and the reader list are written together — the one
       * invariant the api holds too: the count is the length of the list.
       */
      readers.set(row.id, [
        { id: QA_MEMBER.id, name: QA_MEMBER.name, photoUrl: QA_MEMBER.photoUrl, readAt: new Date().toISOString() },
      ]);
      row.viewsCount = 1;
      // … and the **write** answers without one, as the api used to. The card fills it in.
      return { ...row, creator: null };
    },
    async createReply({ parentId, body, mentions }) {
      const reply = update({
        id: `F-U-reply-${String(nextId++)}`,
        itemId,
        parentId,
        bodyHtml: html(body, mentions),
        bodyText: text(body),
        createdAt: new Date().toISOString(),
        mentions: [...mentions],
      });
      updates = updates.map((row) =>
        row.id === parentId ? { ...row, replies: [...row.replies, reply] } : row,
      );
      // P-303: the reply's answer carries no creator either — the card names its own author.
      return { ...reply, creator: null };
    },
    async editUpdate({ updateId, body, mentions }) {
      const now = new Date().toISOString();
      const apply = (row: CardUpdate): CardUpdate =>
        row.id === updateId
          ? { ...row, bodyHtml: html(body, mentions), bodyText: text(body), editedAt: now, updatedAt: now }
          : { ...row, replies: row.replies.map(apply) };
      updates = updates.map(apply);
      return find(updateId)!;
    },
    async deleteUpdate({ updateId }) {
      updates = updates
        .filter((row) => row.id !== updateId)
        .map((row) => ({ ...row, replies: row.replies.filter((reply) => reply.id !== updateId) }));
    },
    async setLiked({ updateId, liked }) {
      const apply = (row: CardUpdate): CardUpdate => {
        const next =
          row.id === updateId
            ? {
                ...row,
                likes: liked
                  ? [...row.likes.filter((like) => like.userId !== ME.id), { userId: ME.id, reactionType: 'like', createdAt: new Date().toISOString() }]
                  : row.likes.filter((like) => like.userId !== ME.id),
              }
            : row;
        return { ...next, replies: next.replies.map(apply) };
      };
      updates = updates.map(apply);
    },
    async setPinned({ updateId, pinned }) {
      updates = updates.map((row) =>
        row.id === updateId ? { ...row, pinned, pinnedAt: pinned ? new Date().toISOString() : null } : row,
      );
    },
    async uploadFile({ file, updateId }) {
      const asset: CardAsset = {
        id: `F-A-${String(nextId++)}`,
        name: file.name,
        mime: file.type || 'application/octet-stream',
        size: file.size,
        url: `/files/pending`,
        thumbnailUrl: null,
        origin: updateId ? 'update' : 'item',
        createdAt: new Date().toISOString(),
      };
      assets = [asset, ...assets];
      if (updateId) {
        updates = updates.map((row) =>
          row.id === updateId ? { ...row, assets: [...row.assets, asset] } : row,
        );
      }
      return asset;
    },

    /**
     * P-299 — the fixture's half of `updates.attachAsset`: an asset that was uploaded while the
     * update was still being written becomes one of that update's assets, which is what the
     * feed then paints inline (P-028).
     */
    async attachAsset({ updateId, assetId }) {
      const asset = assets.find((row) => row.id === assetId);
      if (!asset) return;
      const attached: CardAsset = { ...asset, origin: 'update' };
      assets = assets.map((row) => (row.id === assetId ? attached : row));
      const apply = (row: CardUpdate): CardUpdate => ({
        ...row,
        assets: row.id === updateId && !row.assets.some((entry) => entry.id === assetId)
          ? [...row.assets, attached]
          : row.assets,
        replies: row.replies.map(apply),
      });
      updates = updates.map(apply);
    },

    /*
     * P-060, the card's "…" Options, in the fixture: the harness has one item on one board, so
     * these move the *name* the header reports and record nothing else. `/__ui/board` exists to
     * make the card's geometry testable with no api (M0-F7 §10, P-002); the actions' behaviour
     * is the api's and is covered by `verify-actions-local.mjs`.
     */
    async renameItem({ name }) {
      currentName = name;
    },
    async duplicateItem() {},
    async archiveItem() {},
    /**
     * **P-304, the card's half.** `items.remove` is the single-item procedure, and it takes a
     * **subitem's** id just as happily as an item's — the service resolves the row's board,
     * which for a subitem is the subitems board `[S1§2]`. So the harness models exactly that:
     * a delete aimed at one of this parent's subitems takes it off the section's list, and
     * `restoreItem` — the Undo — puts it back where it was.
     */
    async deleteItem({ itemId: target }) {
      const going = subitems.find((row) => row.id === target);
      if (!going) return;
      removedSubitems.set(target, { row: going, at: subitems.indexOf(going) });
      subitems = subitems.filter((row) => row.id !== target);
    },
    async moveItemToGroup() {},
    async moveItemToBoard() {},
    /* P-164: the harness has nothing to put back, so the inverses are accepted and ignored. */
    async unarchiveItem() {},
    async restoreItem({ itemId: target }) {
      const back = removedSubitems.get(target);
      if (!back) return;
      removedSubitems.delete(target);
      const next = [...subitems];
      next.splice(Math.min(back.at, next.length), 0, back.row);
      subitems = next;
    },
    /* P-162: one subitem, so the section's rows, its empty state and `+ Add subitem` all paint. */
    async subitemsOf() {
      // P-308: the refusal the pilot's unreachable subitems board answers with.
      if (options.subitemsError !== undefined) throw new Error(options.subitemsError);
      return subitems;
    },
    async createSubitem({ name }) {
      if (options.subitemsError !== undefined) throw new Error(options.subitemsError);
      const created = { id: `F-SUBITEM-${String(subitems.length + 1)}`, name, boardId: 'F-BOARD-SUB' };
      subitems = [...subitems, created];
      return created;
    },
    async boardGroups() {
      return [
        { id: 'topics', title: 'Open Requests' },
        { id: 'next', title: 'Next week' },
      ];
    },
    async boards() {
      return [
        { id: boardId, name: boardName },
        { id: 'F-BOARD-2', name: 'Actions target board' },
      ];
    },
  };
}
